<template>
  <div style="position:absolute;top:0;left:0;right:0;bottom:0;" class="layout-column">
    <div class="layout-column flex ma-2 elevation-5"
         style="border-radius:4px;position:relative;"
         v-bind:style="{background: compColor.contentFrame}">

      <ValidationObserver ref="EmployeeDetailForm" v-slot="{ valid, pristine, reset }" class="layout-column flex">
        <div v-bind:style="{'z-index': '4'}" class="layout-row layout-align-center-center pa-1" v-if="!employeeId">
          <div class="layout-row">
            <lx-btn small classes="mr-1 text-none"
                    :disabled="pristine"
                    @click="clearForm(reset)"
                    icon="mdi-undo-variant"
                    tooltip="Änderungen zurücksetzen"
                    label="Zurücksetzen"
                    hide-label></lx-btn>
            <lx-btn small classes="mr-1 text-none"
                    :disabled="!valid || pristine"
                    @click="saveEmployeeData"
                    icon="save"
                    tooltip="Mitarbeiter anlegen"
                    label="Speichern"
                    hide-label></lx-btn>
          </div>
          <div class="flex layout-row"></div>
        </div>

        <v-divider v-bind:style="{'border-color':compColor.lightDivider}"></v-divider>

        <div class="flex layout-column pa-2" style="overflow-y: auto;">
          <v-progress-linear :indeterminate="loading" v-if="loading" color="red accent-2"
                             background-color="red lighten-4" class="ma-0"
                             style="border-radius:0;height:4px;position: absolute;top:56px;left:0; width:100%;"></v-progress-linear>

          <lx-expansion-panels :single-open="false" v-model="panel">
            <lx-expansion-panel :disabled="true">
              <lx-expansion-panel-header icon="mdi-account-circle"
                                         :disabled="true"
                                         :main-headline="$t('COMPONENT_ACCOUNT.HEADER')"
                                         :sub-headline="$t('COMPONENT_ACCOUNT.BASE_DATA_SUB_LINE')">
              </lx-expansion-panel-header>
              <lx-expansion-panel-content>
                <div class="pa-4 layout-row layout-wrap">
                  <div class="layout-row layout-wrap flex-100">

                    <!-- email -->

                    <div class="flex-gt-sm-50 flex-100 px-2">
                      <validation-provider tag="div" ref="email" name="email"
                                           rules="required|email"
                                           v-slot="{ errors }">
                        <v-text-field
                          :dark="compColor.isInDarkMode"
                          v-model="model.email"
                          :label="$t('COMPONENT_ACCOUNT.EMAIL')"
                          :hint="$t('COMPONENT_USER_DETAILS.CHECK_MAIL_HINT')"
                          :color="compColor.isInDarkMode ? compColor.primeHighlightColor : compColor.primeColor"
                          :disabled="!!employeeId || !!(authUserExists === false && localUserExists === false && model.email)"
                          :error-messages="errors[0]"
                          :dense="denseFields"
                          :hide-details="!errors || errors.length <= 0"
                          :persistent-hint="!employeeId"
                          outlined
                        ></v-text-field>
                      </validation-provider>
                    </div>

                    <!-- Button to check mail of new user, will not be shown when existing user  -->

                    <lx-btn :dark="compColor.isInDarkMode" class="white--text text-none ml-2 pa-5" :color="compColor.primeColor"
                            :disabled="!model.email || isMailInvalid || !!(authUserExists === false && localUserExists === false && model.email)"
                            v-if="!employeeId"
                            :width="300"
                            :height="denseFields ? 40 : 48"
                            @click="checkEmail"
                            style="height: 60%;"
                            :label="$t('buttons.check_email.title')">
                      <v-icon left dark>search</v-icon>
                      <span>{{ $t('buttons.check_email.value') }}</span>
                    </lx-btn>
                  </div>

                  <!-- alert when recent auth user (email exists at lx-auth), only when new user -->

                  <div v-show="!employeeId && authUserExists && !localUserExists" class="ml-2 pt-4 flex-100">
                    <div class="layout-row wrap">
                      <div class="flex xs12">
                        <lx-alert-box color="orange"
                                      class="layout wrap flex-gt-sm-100 flex-100 px-2 mt-1 py-1">
                          <div class="layout-row layout-align-start-center flex">
                            <div class="layout-column">
                              <span>{{ $t('COMPONENT_USER_DETAILS.AUTH_EMPLOYEE_ALREADY_EXISTS') }}</span>
                              <span style="font-weight: bold;">{{ $t('COMPONENT_USER_DETAILS.AUTH_USER_ALREADY_EXISTS_HINT') }}</span>
                            </div>

                            <div class="flex"></div>

                            <lx-btn
                              color="orange darken-3"
                              depressed
                              classes="mx-3 text-none"
                              @click="addEmployee"
                              width="auto"
                              small
                              :label="$t('buttons.add_employee.value')">
                            </lx-btn>
                          </div>
                        </lx-alert-box>
                      </div>
                    </div>
                  </div>

                  <!-- alert when recent auth & local user (email exists at lx-auth & local), only when new user -->

                  <div v-show="!employeeId && localUserExists" class="ml-2 pt-2 flex-100">
                    <div class="layout-row wrap">
                      <div class="flex xs12">
                        <lx-alert-box color="red"
                                      class="layout wrap flex-gt-sm-100 flex-100 px-2 mt-1 py-1">
                          {{ $t('COMPONENT_USER_DETAILS.LOCAL_USER_ALREADY_EXISTS') }}
                        </lx-alert-box>
                      </div>
                    </div>
                  </div>

                  <!-- new user (email NOT exists at lx-auth) or existing user with disabled fields-->

                  <div v-show="employeeId || (authUserExists === false && localUserExists === false && model.email)" class="layout-row layout-wrap flex-100 mt-3">

                    <div class="flex-100 px-3 py-1 mb-6 mx-2"
                         :class="isDarkMode ? 'blue-grey darken-3': compColor.prime + ' lighten-5'"
                         style="border-radius: 4px;">
                      <span class="body-2 font-weight-bold" :style="{color: compColor.primeColor}">Notwendige Grunddaten</span>
                    </div>

                    <!-- salutation -->

                    <div class="flex-gt-sm-50 flex-100 px-2">
                      <validation-provider tag="div" name="salutation">
                        <v-select
                          :dark="compColor.isInDarkMode"
                          :color="compColor.primeColor"
                          :dense="denseFields"
                          :label="$t('COMPONENT_ACCOUNT.SALUTATION')"
                          outlined
                          hide-details
                          :disabled="!!employeeId"
                          :items="salutations"
                          v-model="model.salutation"
                          :menu-props="{ bottom: true, offsetY: true }">
                          <template v-slot:item="{ item, on, attrs }">
                            <v-list-item v-on="on"
                                         :class="attrs.inputValue ? [compColor.prime, 'lighten-4'] : compColor.isInDarkMode?['blue-grey darken-4'] : []">
                              <v-list-item-content class="py-0">
                                <label style="cursor:pointer;"
                                       :class="attrs.inputValue ? [compColor.primeClass] : compColor.isInDarkMode ? ['white--text'] : []"
                                       class="lighten-4 text--darken-2">{{ item.text }}</label>
                              </v-list-item-content>
                            </v-list-item>
                          </template>
                        </v-select>
                      </validation-provider>
                    </div>

                    <div class="flex-gt-sm-50 flex-100 px-2"></div>

                    <!-- firstname -->

                    <div class="flex-gt-sm-50 flex-100 px-2">
                      <validation-provider tag="div" name="firstname" rules="required|min:2|name_string" v-slot="{ errors }">
                        <v-text-field
                          :dark="compColor.isInDarkMode"
                          :color="compColor.primeColor"
                          :dense="denseFields"
                          :label="$t('COMPONENT_ACCOUNT.FIRSTNAME') + ' *'"
                          outlined
                          :hide-details="!errors || errors.length <= 0"
                          :disabled="!!employeeId"
                          :error-messages="errors[0]"
                          v-model="model.firstname"
                        ></v-text-field>
                      </validation-provider>
                    </div>

                    <!-- lastname -->

                    <div class="flex-gt-sm-50 flex-100 px-2">
                      <validation-provider tag="div" name="lastname" rules="required|min:2|name_string" v-slot="{ errors }">
                        <v-text-field
                          :dark="compColor.isInDarkMode"
                          :color="compColor.primeColor"
                          :dense="denseFields"
                          :label="$t('COMPONENT_ACCOUNT.LASTNAME') + ' *'"
                          outlined
                          :hide-details="!errors || errors.length <= 0"
                          :disabled="!!employeeId"
                          :error-messages="errors[0]"
                          v-model="model.lastname"
                        ></v-text-field>
                      </validation-provider>
                    </div>

                    <!-- active state -->

                    <div class="flex-gt-sm-100 flex-100 px-2 mb-2">
                      <fake-input-frame class="mb-3 flex layout-column"
                                        :dense="denseFields"
                                        label="Nutzeraktivierung">
                        <div class="layout-row layout-align-start-center px-2 mt-n2" :style="{height: denseFields ? '40px':'50px'}">
                          <v-switch
                            v-model="model.is_active"
                            class="ml-2 my-0"
                            dense
                            inset
                            @change="changeActiveState"
                            :color="compColor.prime"
                            :dark="isDarkMode"
                            hide-details
                          >
                            <template #label>
                              <span class="body-2 blue-grey--text text--lighten-2">{{ $t('COMPONENT_ACCOUNT.IS_ACTIVE') }}</span>
                            </template>
                          </v-switch>
                        </div>
                      </fake-input-frame>
                    </div>
                  </div>
                </div>
              </lx-expansion-panel-content>
            </lx-expansion-panel>
          </lx-expansion-panels>
        </div>
      </ValidationObserver>
    </div>
  </div>
</template>

<script>
import lodash from 'lodash'

export default {
  name: 'EmployeeDetails',
  props: ['employeeId', 'tabid'],
  data () {
    return {
      e1: true,
      loading: false,
      model: {},
      oldModel: {},
      panel: [0],
      error: {},
      errorDialog: false,
      authUserExists: null,
      localUserExists: null,
      salutations: [
        { value: null, text: '' },
        { value: 'Herr', text: this.$t('COMPONENT_ACCOUNT.SALUTATIONS.MR') },
        { value: 'Frau', text: this.$t('COMPONENT_ACCOUNT.SALUTATIONS.MRS') },
        { value: 'Unbekannt', text: this.$t('COMPONENT_ACCOUNT.SALUTATIONS.UNDEFINED') }
      ],
    }
  },
  computed: {
    isMailInvalid () {
      return this.$refs.EmployeeDetailForm.errors && this.$refs.EmployeeDetailForm.errors.email && this.$refs.EmployeeDetailForm.errors.email.length > 0
    }
  },
  created () {
    if (this.employeeId) {
      this.getUserData()
    } else {
      this.model.is_active = true
      this.oldModel = lodash.cloneDeep(this.model)
    }
  },
  methods: {
    /**
     * Reset form
     */
    clearForm (reset) {
      reset() // reset validation
      this.model = lodash.cloneDeep(this.oldModel)

      // reset flags to hide alerts if exists
      this.authUserExists = null
      this.localUserExists = null
    },

    /**
     * Check if email already exists in auth and/or application
     */
    checkEmail () {
      if (!this.employeeId && this.model.email) {
        if (this.loading) return
        this.loading = true

        this.$http.get('/employees/checkAuthUserExists/' + this.model.email)
          .then((res) => {
            if (res.data) {
              this.$set(this, 'authUserExists', res.data.authUser)
              this.$set(this, 'localUserExists', res.data.localUser)
            }
          })
          .catch(this.NotifyError)
          .finally(() => {
            this.loading = false
          })
      }
    },

    /**
     * Add user to application if auth-user already exists
     */
    addEmployee () {
      if (!this.employeeId && this.model.email) {
        if (this.loading) return
        this.loading = true

        this.$http.post('/employees/addExistingAuthUser', { email: this.model.email, userType: 'employee' })
          .then(() => {
            this.loading = false

            // remove current tab from store
            if (this.tabid || this.$route?.params?.tabid) {
              this.removeTabFromStoreByTabid(this.tabid || this.$route.params.tabid)
            }

            // search 'EmployeeList' tab and set them as active tab
            for (let i = 0; i < this.getTabsFromStore.length; i++) {
              if (this.getTabsFromStore[i].routeName === 'EmployeesList') {
                this.setActiveTab(i)
                break
              }
            }
          })
          .catch(this.NotifyError)
          .finally(() => {
            this.loading = false
          })
      }
    },

    /**
     * Save new employee to auth and users
     */
    saveEmployeeData () {
      if (this.loading) return
      this.loading = true

      // create auth user
      const data = {
        email: this.model.email,
        login_name: this.model.login_name,
        password: this.model.password,
        is_active: this.model.is_active,
        firstname: this.model.firstname,
        lastname: this.model.lastname,
        salutation: this.model.salutation,
        user_type: 'employee'
      }

      this.$http.post('/employees', data)
        .then(() => {
          this.loading = false

          // remove current tab from store
          if (this.tabid || this.$route?.params?.tabid) {
            this.removeTabFromStoreByTabid(this.tabid || this.$route.params.tabid)
          }

          // search 'EmployeeList' tab and set them as active tab
          for (let i = 0; i < this.getTabsFromStore.length; i++) {
            if (this.getTabsFromStore[i].routeName === 'EmployeesList') {
              this.setActiveTab(i)
              break
            }
          }
        })
        .catch((error) => {
          this.populateFormError(this.$refs.EmployeeDetailForm, error)
            .catch(this.NotifyError)
        })
        .finally(() => {
          this.loading = false
        })
    },

    /**
     * Get data of existing employee
     */
    getUserData () {
      if (this.loading) return
      this.loading = true

      this.$http.get('/employees/id/' + this.employeeId)
        .then(res => {
          if (res.data) {
            this.model = res.data
            this.oldModel = lodash.cloneDeep(this.model)
          }
        })
        .catch(this.NotifyError)
        .finally(() => {
          this.loading = false
        })
    },

    /**
     * Disable or enable already existing employee for application
     */
    changeActiveState () {
      // update is_active state for user
      if (this.employeeId) {
        if (this.loading) return
        this.loading = true

        this.$http.patch('/employees/changeActiveState', { user_id: this.employeeId, is_active: this.model.is_active })
          .then((res) => {
            this.NotifySuccess(res, this.$t(this.model.is_active ? 'COMPONENT_ACCOUNT.USER_SUCCESSFULLY_ACTIVATED' : 'COMPONENT_ACCOUNT.USER_SUCCESSFULLY_DEACTIVATED'))
          })
          .catch(this.NotifyError)
          .finally(() => {
            this.loading = false
          })
      }
    },
  }
}
</script>
